import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, CalendarCheck, Loader2 } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import { API, EVENT_TYPES } from "../../lib/site";

const EMPTY = {
  name: "", phone: "", email: "", event_type: "", event_date: "", event_location: "",
  guests: "", duration: "", setting: "", sound: "", budget: "", notes: "",
};

const inputCls = "tk-input";
const labelCls = "tk-label";

export default function Booking({ presetType }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [avail, setAvail] = useState({ name: "", contact: "", event_date: "", event_location: "", event_type: "" });
  const [availBusy, setAvailBusy] = useState(false);

  useEffect(() => {
    if (presetType) setForm((f) => ({ ...f, event_type: presetType }));
  }, [presetType]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent! The Kroonerz team will get back to you shortly.");
      setForm(EMPTY);
    } catch {
      toast.error("Something went wrong. Please WhatsApp or call us instead — 95556 07090.");
    } finally {
      setBusy(false);
    }
  };

  const submitAvail = async (e) => {
    e.preventDefault();
    setAvailBusy(true);
    try {
      await axios.post(`${API}/availability`, avail);
      toast.success("Availability request sent! We'll confirm your date personally.");
      setAvail({ name: "", contact: "", event_date: "", event_location: "", event_type: "" });
    } catch {
      toast.error("Something went wrong. Please WhatsApp or call us instead — 95556 07090.");
    } finally {
      setAvailBusy(false);
    }
  };

  return (
    <section id="book" className="relative overflow-hidden py-20 lg:py-28" data-testid="booking-section">
      <div className="spotlight-gold pointer-events-none absolute left-[-15%] top-[-10%] h-[500px] w-[500px]" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHead
          eyebrow="Book Us · Request a Quote"
          title="Planning an Event? Let's Make It Musical."
          sub="Tell us a little about your event and we'll get back to you to discuss the performance, requirements and quotation."
        />
        <div className="mt-14 grid gap-8 lg:grid-cols-3">
          <Reveal className="lg:col-span-2">
            <form onSubmit={submit} className="tk-card gold-frame grid gap-5 p-6 sm:grid-cols-2 sm:p-8" data-testid="enquiry-form">
              <div>
                <label className={labelCls} htmlFor="eq-name">Name *</label>
                <input id="eq-name" data-testid="enquiry-name-input" required className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" />
              </div>
              <div>
                <label className={labelCls} htmlFor="eq-phone">Phone *</label>
                <input id="eq-phone" data-testid="enquiry-phone-input" required type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="Your phone number" />
              </div>
              <div>
                <label className={labelCls} htmlFor="eq-email">Email</label>
                <input id="eq-email" data-testid="enquiry-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" />
              </div>
              <div>
                <label className={labelCls} htmlFor="eq-type">Event Type</label>
                <select id="eq-type" data-testid="enquiry-type-select" className={inputCls} value={form.event_type} onChange={set("event_type")}>
                  <option value="">Select event type</option>
                  {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls} htmlFor="eq-date">Event Date</label>
                <input id="eq-date" data-testid="enquiry-date-input" type="date" className={`${inputCls} [color-scheme:dark]`} value={form.event_date} onChange={set("event_date")} />
              </div>
              <div>
                <label className={labelCls} htmlFor="eq-location">Event Location</label>
                <input id="eq-location" data-testid="enquiry-location-input" className={inputCls} value={form.event_location} onChange={set("event_location")} placeholder="e.g. Chandigarh, Sector 17" />
              </div>
              <div>
                <label className={labelCls} htmlFor="eq-guests">Expected Guests</label>
                <select id="eq-guests" data-testid="enquiry-guests-select" className={inputCls} value={form.guests} onChange={set("guests")}>
                  <option value="">Select range</option>
                  {["Under 50", "50 – 150", "150 – 300", "300+"].map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls} htmlFor="eq-duration">Performance Duration</label>
                <select id="eq-duration" data-testid="enquiry-duration-select" className={inputCls} value={form.duration} onChange={set("duration")}>
                  <option value="">Select duration</option>
                  {["1 – 2 hours", "3 – 4 hours", "Full evening", "Flexible / not sure"].map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls} htmlFor="eq-setting">Indoor / Outdoor</label>
                <select id="eq-setting" data-testid="enquiry-setting-select" className={inputCls} value={form.setting} onChange={set("setting")}>
                  <option value="">Select setting</option>
                  {["Indoor", "Outdoor", "Not sure yet"].map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls} htmlFor="eq-sound">Sound Requirements</label>
                <select id="eq-sound" data-testid="enquiry-sound-select" className={inputCls} value={form.sound} onChange={set("sound")}>
                  <option value="">Select option</option>
                  {["Use the group's own setup", "We will arrange sound", "Need help deciding"].map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="eq-budget">Approximate Budget</label>
                <select id="eq-budget" data-testid="enquiry-budget-select" className={inputCls} value={form.budget} onChange={set("budget")}>
                  <option value="">Prefer to select</option>
                  {["Under ₹25,000", "₹25,000 – ₹50,000", "₹50,000 – ₹1,00,000", "Above ₹1,00,000", "Prefer to discuss"].map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="eq-notes">Additional Requirements</label>
                <textarea id="eq-notes" data-testid="enquiry-notes-input" rows={4} className={inputCls} value={form.notes} onChange={set("notes")} placeholder="Song requests, occasion details, anything else we should know…" />
              </div>
              <div className="sm:col-span-2">
                <button type="submit" disabled={busy} data-testid="enquiry-submit-button" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-bold text-navy-950 transition-colors hover:bg-gold-light disabled:opacity-60 sm:w-auto">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Submit Enquiry
                </button>
              </div>
            </form>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="flex h-full flex-col gap-6">
              <form onSubmit={submitAvail} className="tk-card flex-1 p-6 sm:p-8" data-testid="availability-form">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gold/10 text-gold"><CalendarCheck className="h-5 w-5" /></span>
                  <h3 className="font-serif text-xl font-semibold text-ivory">Check Availability</h3>
                </div>
                <p className="mt-3 text-xs leading-relaxed text-slate-400">
                  Share your date — we'll confirm availability personally over call or WhatsApp.
                </p>
                <div className="mt-5 space-y-4">
                  <input required data-testid="availability-name-input" className={inputCls} placeholder="Your name *" value={avail.name} onChange={(e) => setAvail((a) => ({ ...a, name: e.target.value }))} />
                  <input required data-testid="availability-contact-input" className={inputCls} placeholder="Phone or email *" value={avail.contact} onChange={(e) => setAvail((a) => ({ ...a, contact: e.target.value }))} />
                  <input required type="date" data-testid="availability-date-input" className={`${inputCls} [color-scheme:dark]`} value={avail.event_date} onChange={(e) => setAvail((a) => ({ ...a, event_date: e.target.value }))} />
                  <input data-testid="availability-location-input" className={inputCls} placeholder="Event location" value={avail.event_location} onChange={(e) => setAvail((a) => ({ ...a, event_location: e.target.value }))} />
                  <select data-testid="availability-type-select" className={inputCls} value={avail.event_type} onChange={(e) => setAvail((a) => ({ ...a, event_type: e.target.value }))}>
                    <option value="">Event type</option>
                    {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <button type="submit" disabled={availBusy} data-testid="availability-submit-button" className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-gold/50 bg-gold/10 px-6 py-3.5 text-sm font-bold text-gold transition-colors hover:bg-gold hover:text-navy-950 disabled:opacity-60">
                    {availBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarCheck className="h-4 w-4" />}
                    Check Availability
                  </button>
                </div>
              </form>
              <div className="rounded-2xl border border-burgundy/50 bg-burgundy/20 p-6" data-testid="booking-quote-note">
                <p className="font-serif text-lg italic text-gold-light">"Every event is different."</p>
                <p className="mt-2 text-xs leading-relaxed text-slate-300">
                  That's why we quote personally — based on performers, duration, location and sound.
                  No fixed packages.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
